// ============================================================
// TMR Portal Admin — conferir e enviar o contrato para assinatura
// ============================================================
import { useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn, Segmented } from '../../components/primitives';
import { Notice } from '../../components/Notice';
import { Icons, StrokeIcon } from '../../lib/icons';
import { useContractPreview, useRequestSignature } from '../../data/contracts/queries';
import type {
  RequestSignatureResponse,
  SignatureDeliveryChannel,
} from '../../data/contracts/contract';
import { ContractPreviewPanel } from './ContractPreviewPanel';
import { OverlayPortal } from '../../components/OverlayPortal';

/** Props for {@link SendContractModal}. */
interface SendContractModalProps {
  contractId: string;
  onClose: () => void;
  /** Called after the provider accepted the document, with the signing link when it gives one. */
  onSent?: (response: RequestSignatureResponse) => void;
  /** Rendered above the preview — the creation flow uses it to say the contract already exists. */
  lead?: ReactNode;
  /** Step wording for the creation flow, which reaches this as step 2 of 2. */
  eyebrow?: string;
}

/**
 * The confirmation step between deciding to send and sending.
 *
 * Sending puts a document in a client's inbox and, once there, calling it off means going to the provider —
 * so it is the one action in this area that should never happen from a single click. It used to happen from
 * three: the row's 34px icon, the detail panel's footer, and the creation modal, none of which asked
 * anything and none of which showed more than two of the seven values the contract prints.
 *
 * This is now the single door. The confirm button is gated on the backend's own verdict (`ready`) rather
 * than on a rule restated here — the server decides what it will accept, and a screen that made up its own
 * version of that would eventually disagree with it.
 *
 * **It renders through `OverlayPortal` (a portal to `document.body`), and that is load-bearing** (`09` §11,
 * N3). One of its three callers is the contract detail, which lives inside `Drawer` — and `Drawer` animates with
 * `transform`, which makes it a containing block for `position: fixed` descendants. Rendered in place, this
 * overlay resolved its `inset: 0` against the drawer instead of the viewport, so the same modal that is
 * centred and roomy from the list came up boxed into a side panel, with the seven values behind a scroll.
 * The portal is what makes one component look the same from all three doors.
 */
export function SendContractModal({
  contractId,
  onClose,
  onSent,
  lead,
  eyebrow = 'Enviar para assinatura',
}: SendContractModalProps) {
  const [sent, setSent] = useState<RequestSignatureResponse | null>(null);
  // Null until the operator touches the selector, so the server's default can win the first render without
  // an effect racing the query. Once they choose, their choice stands even if a refetch would suggest
  // otherwise — the point of the control is that it is theirs.
  const [picked, setPicked] = useState<SignatureDeliveryChannel | null>(null);
  // Once it is gone the preview is meaningless — it describes a document that now exists — so the query is
  // switched off rather than left to refetch behind the success message.
  const preview = useContractPreview(contractId, sent === null);
  const requestSignature = useRequestSignature();

  const stop = (e: MouseEvent) => e.stopPropagation();
  const error = requestSignature.error instanceof ApiError ? requestSignature.error : null;

  const channels = preview.data?.channels ?? [];
  // The preview says which channel works; the operator's pick overrides it. Falling back to WhatsApp keeps
  // the type honest while `channels` is still empty — nothing is sendable in that state anyway.
  const channel = picked ?? preview.data?.defaultChannel ?? 'WhatsApp';
  const selected = channels.find((c) => c.channel === channel);

  // The server's verdict for the channel actually chosen, never a rule restated here. `channels` is empty
  // when the Empresa could not be resolved, and then nothing is sendable.
  const ready = selected?.ready ?? false;

  const send = () => {
    requestSignature.mutate(
      { contractId, channel },
      {
        onSuccess: (response) => {
          // It does not close itself: the operator sees where it went and the signing link. The link is not
          // stored, but it can be read again from the contract while it awaits signature.
          setSent(response);
          onSent?.(response);
        },
      },
    );
  };

  return (
    <OverlayPortal>
      <div onClick={onClose} style={overlayStyle}>
        <div onClick={stop} style={panelStyle}>
          <header style={headerStyle}>
            <div>
              <div className="pa-eyebrow" style={{ marginBottom: 4 }}>
                {eyebrow}
              </div>
              <h3 style={titleStyle}>{sent ? 'Enviado para assinatura' : 'Confira antes de enviar'}</h3>
            </div>
            <button type="button" onClick={onClose} className="pa-icon-btn" style={{ width: 34, height: 34 }}>
              <StrokeIcon d={Icons.x} size={18} />
            </button>
          </header>

          <div style={bodyStyle}>
            {sent ? (
              <Notice tone="success">
                Documento criado no ClickSign
                {sent.contract.signatureDeliveryTarget ? (
                  <>
                    {' '}
                    e enviado {sent.contract.signatureDeliveryChannel === 'WhatsApp'
                      ? 'por WhatsApp'
                      : 'por e-mail'}{' '}
                    para <strong>{sent.contract.signatureDeliveryTarget}</strong>.
                  </>
                ) : (
                  ' e signatários notificados.'
                )}
                {sent.signatureUrl ? (
                  <>
                    {' '}
                    O link não fica guardado aqui, mas pode ser consultado de novo no contrato enquanto ele
                    aguarda assinatura (<strong>Ver link</strong>).
                    <div style={{ marginTop: 10 }}>
                      <a href={sent.signatureUrl} target="_blank" rel="noreferrer noopener" style={linkStyle}>
                        {sent.signatureUrl}
                      </a>
                    </div>
                  </>
                ) : (
                  ' O provedor não devolveu link direto; o signatário recebe pelo canal escolhido.'
                )}
              </Notice>
            ) : (
              <>
                {lead}
                <ContractPreviewPanel query={preview} channel={channel} />

                {/* Only once there is something to choose between. While the preview loads, or when the
                    Empresa could not be resolved at all, a selector would be offering a choice about
                    nothing. */}
                {channels.length > 0 && (
                  <section style={{ marginTop: 20 }}>
                    <div className="pa-form-label" style={{ marginBottom: 8 }}>
                      Como enviar
                    </div>
                    <Segmented<SignatureDeliveryChannel>
                      size="sm"
                      value={channel}
                      onChange={setPicked}
                      options={channels.map((c) => ({
                        value: c.channel,
                        label: c.channel === 'WhatsApp' ? 'WhatsApp' : 'E-mail',
                        // Shown and refused, never hidden: an option that vanishes makes "por que não tem
                        // WhatsApp aqui?" unanswerable. The reason is right below.
                        disabled: !c.available,
                      }))}
                    />
                    {selected?.target && (
                      <div style={targetStyle}>
                        Vai para <strong>{selected.target}</strong>
                      </div>
                    )}
                    {/* Wording comes from the backend, like every other issue on this screen — only it knows
                        whether the block is the account's configuration or a blank field in the cadastre. */}
                    {channels
                      .flatMap((c) => c.issues)
                      .map((issue) => (
                        <Notice key={issue.code} tone="warning">
                          {issue.message}
                        </Notice>
                      ))}
                  </section>
                )}
              </>
            )}

            {error && (
              <Notice tone="danger">
                {error.code === 'conflict'
                  ? 'Este contrato já havia sido enviado ou já está encerrado.'
                  : error.code === 'validation'
                    ? 'O envio foi recusado por um dado do cadastro da empresa.'
                    : 'O provedor de assinatura recusou o envio — nada foi perdido, e o contrato segue pendente.'}{' '}
                {error.detail}
              </Notice>
            )}
          </div>

          <footer style={footerStyle}>
            <span style={{ fontSize: 12, color: 'var(--fg-subtle)', maxWidth: 340 }}>
              {sent
                ? 'Enquanto aguarda assinatura, o contrato pode ser reenviado ou mandado para outro contato.'
                : 'Enviar cria o documento no ClickSign e o coloca na frente dos signatários.'}
            </span>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn variant="secondary" onClick={onClose}>
                {sent ? 'Concluir' : 'Fechar'}
              </Btn>
              {!sent && (
                <Btn
                  variant="primary"
                  icon={requestSignature.isPending ? undefined : Icons.send}
                  onClick={requestSignature.isPending || !ready ? undefined : send}
                  disabled={requestSignature.isPending || !ready}
                >
                  {requestSignature.isPending
                    ? 'Enviando…'
                    : requestSignature.error
                      ? 'Tentar novamente'
                      : 'Confirmar e enviar'}
                </Btn>
              )}
            </div>
          </footer>
        </div>
      </div>
    </OverlayPortal>
  );
}

const overlayStyle = {
  position: 'fixed' as const,
  inset: 0,
  zIndex: 70,
  background: 'rgba(28,30,28,0.32)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};

const panelStyle = {
  // Wide enough for the seven values the contract prints to be read at once. It used to be 560, which fit
  // the drawer it was wrongly nested in and nothing else (`09` §11, N3).
  width: 760,
  maxWidth: '100%',
  maxHeight: '88vh',
  background: 'var(--cream-50)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-lg)',
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden',
};

const headerStyle = {
  padding: '20px 26px',
  borderBottom: '1px solid var(--border-default)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 16,
};

const titleStyle = {
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 19,
  margin: 0,
  letterSpacing: '-0.01em',
};

const bodyStyle = { flex: 1, overflowY: 'auto' as const, padding: '18px 26px 22px' };

const targetStyle = {
  marginTop: 8,
  fontSize: 12.5,
  color: 'var(--fg-muted)',
};

const linkStyle = {
  fontFamily: 'var(--font-mono)',
  fontSize: 12.5,
  color: 'var(--forest-800)',
  wordBreak: 'break-all' as const,
};

const footerStyle = {
  padding: '16px 26px',
  borderTop: '1px solid var(--border-default)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 16,
};
